import { message } from 'antd';
import { pushLog2DV2LogServer } from '../../../../../../services/logging';
import { CodeConstant } from '../../../../../constants';
import * as dashboardService from '../../../../../services/Dashboard';
import * as onboardingService from '../../../../../services/Onboarding';
import { OnboardingNewUiUxReduxActions } from '../onboardingNewUiUx.action';

/**
 * Redux action types for role and tech skills
 * @typedef {Object} RoleAndTechSkillReduxActions
 * @property {string} UPDATE_ROLE_TECH_SKILLS_STATE_DATA - Action type for updating role and tech skills state data
 * @property {string} RESET_STATE_DATA - Action type for resetting state data
 */
export const roleAndTechSkillReduxActions = {
    UPDATE_ROLE_TECH_SKILLS_STATE_DATA: 'UPDATE_ROLE_TECH_SKILLS_STATE_DATA',
    API_REQUEST_SUCCESS: 'API_REQUEST_SUCCESS',
    RESET_STATE_DATA: 'RESET_STATE_DATA',
    API_REQUEST_FAIL: 'API_REQUEST_FAIL',
};

export const updateParent = (values) => dispatch => {
    dispatch({
        type: OnboardingNewUiUxReduxActions.UPDATE_SECTION_VALUES,
        payload: {
            page: 'role_skills_page',
            values
        }
    })
};

/**
 * Select top skills by priority for a given role
 * @param {number} roleId - Role ID
 * @param {Object[]} skills_by_role_data - Skills data grouped by role
 * @returns {Object[]} - Array of selected top skills
 */
export const SELECT_TOP_SKILLS_BY_PRIORITY = (roleId, skills_by_role_data) => {
    const role = skills_by_role_data.find(role => role.id === roleId);
    if (!role) return [];
    return role.skills
          .map((skill) => ({ id: skill.id, skill_name: skill.name }))
};

/**
 * Update job role information
 * @param {Object} data - Data to update job role
 * @returns {Promise<void>} - Promise indicating the completion of the operation
 */
const UPDATE_BASIC_INFO = async data => {
    try {
        const response = await dashboardService.updateBasicInfo(data);
        if (!response.data.success) {
            // Log warning if update fails
            const log_error = { message: `Error Response code from update basic info action ${response.status}` };
            pushLog2DV2LogServer.warn('ONBOARDING', 'UPDATE_BASIC_INFO', {}, {}, log_error);
        }
    } catch (e) {
        // Handle errors and log them
        const { errorCode, message, stack } = e;
        pushLog2DV2LogServer.error('ONBOARDING', 'UPDATE_BASIC_INFO', {}, { data }, { errorCode, message, stack });
    }
};

const UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES = async (developerLanguageProficiencies) => {
    try {
      const response = await onboardingService.saveDeveloperLanguageProficiencies({ developerLanguageProficiencies});
      if (response.status === 201) {
        pushLog2DV2LogServer.info('ONBOARDING', 'UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES', {}, developerLanguageProficiencies, null);
      } else {
        message.warn('Error: unable to update language proficiencies');
        const log_error = {
          message: 'Response code from UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES is ' + response.status + '.'
        };
        pushLog2DV2LogServer.warn('ONBOARDING', 'UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES', {}, {}, log_error);
      }
    } catch (e) {
      message.error('Error: unable to update language proficiencies');
      pushLog2DV2LogServer.error('ONBOARDING', 'UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES', {}, {}, e);
    }
};

const REMOVE_DEVELOPER_LANGUAGE = async (data) => {
    try {
      const response = await onboardingService.removeAllLanguages(data);
      if (response.status === 204) {
        pushLog2DV2LogServer.info('ONBOARDING', 'REMOVE_DEVELOPER_LANGUAGE', {}, data, null);
      } else {
        message.warn('Error: unable to remove languages');
        const log_error = {
          message: 'Response code from REMOVE_DEVELOPER_LANGUAGE is ' + response.status + '.'
        };
        pushLog2DV2LogServer.warn('ONBOARDING', 'REMOVE_DEVELOPER_LANGUAGE', {}, {}, log_error);
      }
    } catch (e) {
      message.error('Error: unable to remove languages');
      pushLog2DV2LogServer.error('ONBOARDING', 'REMOVE_DEVELOPER_LANGUAGE', {}, {}, e);
    }
};

/**
 * Handle skill-related actions (add, update, delete)
 * @param {Object|number} skill - Skill data or skill ID
 * @param {Function} actionType - Function representing the action type (add, update, delete)
 * @returns {Promise<void>} - Promise indicating the completion of the operation
 */
const handleSkillAction = async (skill, name, actionType, dispatch, data_update=false, callback=null) => {
    try {
        if (data_update) {
            skill = {
                id: skill.row_id || null,
                skill_id: skill.id,
                skill_name: skill.skill_name,
                skill_level: skill.skill_level,
                score: skill.score
            }
        }
        const response = await actionType(skill);
        if (response.data.code === CodeConstant.OK) {
            dispatch({ type: roleAndTechSkillReduxActions.API_REQUEST_SUCCESS });
            if (callback && typeof callback === 'function') callback(response.data.data)
        }
    } catch (e) {
        // Handle errors and log them
        const { errorCode, message, stack } = e;
        pushLog2DV2LogServer.error('ONBOARDING', name, {}, { skill }, { errorCode, message, stack });
        dispatch({ type: roleAndTechSkillReduxActions.API_REQUEST_FAIL })
    }
};

const VALIDATE_SKILL_DATA = (value, newErrorObject) => {
    if (!value || !value.length) {
        newErrorObject = {
            ...newErrorObject,
            selected_skills_data: ['Select one or more skills'],
        };
    } else {
        const skillHasError = value.filter((skill) => !skill.score || skill.score < 1);
        if (skillHasError.length) {
            const skill_names = skillHasError.map((skill) => skill.skill_name);
            newErrorObject = {
                ...newErrorObject,
                selected_skills_data: [`${skill_names.join(', ')} has zero Year of Experience, update to non-zero or remove.`],
            };
        }
    }
    return newErrorObject;
}

/**
 * Checks if all fields have been successfully completed
 * @returns boolean (false(no error)/true(has error))
 */
export const VALIDATE_ROLE_SKILL_PAGE_DATA = (dispatch, getState) => {
    const state = getState();
    const role_skills_page_data = state.onboarding_new_ui.role_skills_page;
    const { job_role_id, selected_skills_data, yearsOfExperience, developerLanguageProficiencies } = role_skills_page_data.values;
    const { hasErrors } = role_skills_page_data;
    let newErrorObject = { ...hasErrors };
    const data_to_validate = { job_role_id, selected_skills_data, yearsOfExperience, developerLanguageProficiencies };

    Object.keys(data_to_validate).forEach((item) => {
        const value = data_to_validate[item];
        const generalError = !value || value === '';
        delete newErrorObject[item];
        switch (item) {
            case 'selected_skills_data':
                newErrorObject = VALIDATE_SKILL_DATA(selected_skills_data, newErrorObject)
                break;
            case 'yearsOfExperience':
                if (((!parseInt(value, 10) && parseInt(value, 10) !== 0) || parseInt(value, 10) < 0)) {
                    newErrorObject = {
                        ...newErrorObject,
                        [item]: ['required'],
                    };
                }
                break;
            case 'developerLanguageProficiencies':
                if (!value || value.length === 0) {
                    newErrorObject = {
                        ...newErrorObject,
                        [item]: ['required'],
                    };
                }
                break;
            default:
                if (generalError) {
                    newErrorObject = {
                        ...newErrorObject,
                        [item]: ['required'],
                    };
                }
                break;
        }
    });

    dispatch({
        type: OnboardingNewUiUxReduxActions.UPDATE_ERROR_DATA,
        payload: { page: 'role_skills_page', errors: newErrorObject },
    });
};

export const RESET_ERROR_FIELD_STATE = (field, val) => (dispatch, getState) => {
    const state = getState();
    const { hasErrors, values } = state.onboarding_new_ui.role_skills_page;
    let newErrorObject = { ...hasErrors };
    delete newErrorObject[field];

    switch (field) {
        case 'selected_skills_data':
            newErrorObject = VALIDATE_SKILL_DATA(values.selected_skills_data, newErrorObject);
            break;
        case 'yearsOfExperience':
            if (((!parseInt(val, 10) && parseInt(val, 10) !== 0) || parseInt(val, 10) < 0)) {
                newErrorObject = {
                    ...newErrorObject,
                    [field]: ['required'],
                };
            }
            break;
        case 'developerLanguageProficiencies':
            if (!val || val.length === 0) {
                newErrorObject = {
                    ...newErrorObject,
                    [field]: ['required'],
                };
            }
            break;
        default:
            if (!val) {
                newErrorObject = {
                    ...newErrorObject,
                    [field]: ['required'],
                };
            }
            break;
    }

    dispatch({
        type: OnboardingNewUiUxReduxActions.UPDATE_ERROR_DATA,
        payload: { page: 'role_skills_page', errors: newErrorObject },
    });
};




/**
 * API actions for role and tech skills
 * @namespace
 * @property {Function} FETCH_JOB_ROLE_TYPE - Action to fetch job role types
 * @property {Function} FETCH_SKILLS_BY_ROLE - Action to fetch skills by role
 * @property {Function} FETCH_ALL_BASE_SKILLS - Action to fetch all base skills
 * @property {Function} UPDATE_BASIC_INFO - Action to update job role
 * @property {Function} ADD_USER_SKILLS - Action to add user skills
 * @property {Function} UPDATE_USER_SKILLS - Action to update user skills
 * @property {Function} DELETE_USER_SKILLS - Action to delete user skills
 */
export const roleAndTechSkillsAPiActions = {
    UPDATE_BASIC_INFO,
    UPDATE_DEVELOPER_LANGUAGE_PROFICIENCIES,
    REMOVE_DEVELOPER_LANGUAGE,
    ADD_USER_SKILLS: (skill, callback) => (dispatch) => handleSkillAction(skill, 'ADD_USER_SKILL', onboardingService.addUserSkillData, dispatch, true, callback),
    UPDATE_USER_SKILLS: (skill, callback) => (dispatch) => handleSkillAction(skill, 'UPDATE_USER_SKILL', onboardingService.updateUserSkillData, dispatch, true, callback),
    DELETE_USER_SKILLS: skill_id => (dispatch) => handleSkillAction(skill_id, 'DELETE_USER_SKILLS', onboardingService.deleteUserSkillData, dispatch),
};
